import { SurveyLayer } from '../../constants/SurveyLayers';
import {
  BulkPrescriptionJobs,
  LayerValueGrid,
  LayerValues,
  LayerValuesForFields,
  VRMForFields,
} from './SeedingJob/SeedingJobCommonTypes';
import { Interval, Layer, PrescriptionJob } from './PrescriptionJob';
import PrescriptionModelReference from '../../model/prescription/PrescriptionModelReference';
import Field from '../../model/Field';

/**
 * Base contract for prescription models used by the prescription-job orchestration workflow.
 *
 * Implementations define how soil-analysis values become variable-rate maps and how those maps are
 * applied to single and bulk prescription jobs. Methods that throw must be implemented by concrete
 * strategies.
 */
export class PrescriptionJobStrategy {
  /** Layers shown as supporting miniature maps in the prescription UI. */
  miniatureMapSelection: SurveyLayer[] = [];

  /** Primary layer used by the prescription model. */
  baseLayer: Layer = null;

  /**
   * Creates a serializable reference identifying this prescription model and its configuration.
   *
   * @returns A reference that can be persisted with a prescription job.
   */
  toReference(): PrescriptionModelReference {
    throw new Error('Not implemented');
  }

  /**
   * A boolean that decides whether the 'Adjustment required' button will be able to show
   * in the toolbar.
   *
   * @returns A boolean that controls 'Adjustment required' button
   */
  isAdjustmentNeeded(): boolean {
    return true;
  }

  /**
   * Lists all layers required to calculate a variable-rate map.
   *
   * @returns The supporting miniature-map layers followed by the base layer.
   */
  getLayersToFetchValuesFor(): Layer[] {
    return [...this.miniatureMapSelection, this.baseLayer];
  }

  /**
   * Calculates a variable-rate map for one field.
   *
   * @param layerValues Layer-value grids keyed by layer.
   * @param prescription Average prescription rate used by models that scale their output to a target.
   * @returns A grid containing the calculated prescription rate for each coordinate.
   */
  createVRM(layerValues: LayerValues, prescription: number): number[][] {
    throw new Error("Method 'createVRM()' must be implemented.");
  }

  /**
   * Creates the initial classification intervals for one or more variable-rate maps.
   *
   * @param seedingRateMap A single field grid or a collection of field grids.
   * @returns The intervals used to classify prescription rates.
   */
  createInitialIntervals(seedingRateMap: LayerValueGrid | number[][][]): Interval[] {
    throw new Error("Method 'createInitialIntervals()' must be implemented.");
  }

  /**
   * Initializes the overview job in a bulk prescription.
   *
   * @param prescriptionJob Current overview job containing the user's prescription settings.
   * @param vrms Calculated variable-rate maps grouped by field ID.
   * @param firstField First field in bulk prescription jobs.
   * @returns The initialized overview job and the intervals shared by its field jobs.
   */
  initializeSingleJob(
    prescriptionJob: PrescriptionJob,
    vrm: LayerValueGrid,
    layerValues: LayerValues
  ): PrescriptionJob {
    throw new Error("Method 'initializeBulkOverview' must be implemented.");
  }
}

/** Bulk prescriptions start on an overview and share its initial intervals across fields. */
export class OverviewPrescriptionJobStrategy extends PrescriptionJobStrategy {
  readonly hasBulkPrescriptionOverview = true as const;

  initializeBulkOverview(
    prescriptionJob: PrescriptionJob,
    vrms: VRMForFields,
    firstField: Field
  ): { currentJob: PrescriptionJob; intervals: Interval[] } {
    throw new Error("Method 'initializeBulkOverview' must be implemented.");
  }

  /**
   * Initializes or updates every field job in a bulk prescription.
   *
   * @param getJobName Generates a localized job name for a field ID.
   * @param prescriptionJob Overview job containing settings shared by all field jobs.
   * @param bulkJobs Existing field jobs keyed by field ID.
   * @param vrms Calculated variable-rate maps keyed by field ID.
   * @param layerValuesForFields Layer values keyed by field ID and survey layer.
   * @param initialIntervals Classification intervals shared by the field jobs.
   * @returns Initialized field jobs keyed by field ID.
   */
  initializeBulkJobs = (
    getJobName: (fieldId: number | string) => string,
    prescriptionJob: PrescriptionJob,
    bulkJobs: BulkPrescriptionJobs,
    vrms: VRMForFields,
    layerValuesForFields: LayerValuesForFields,
    initialIntervals: Interval[]
  ): BulkPrescriptionJobs => {
    throw new Error("Method 'initializeBulkJobs' must be implemented.");
  };
}

/** Bulk prescriptions open the first field directly; each field initializes its own intervals. */
export class DirectPrescriptionJobStrategy extends PrescriptionJobStrategy {
  readonly hasBulkPrescriptionOverview = false as const;

  initializeBulkJobsWithoutOverview = (
    getJobName: (fieldId: number | string) => string,
    prescriptionJob: PrescriptionJob,
    bulkJobs: BulkPrescriptionJobs,
    vrms: VRMForFields,
    layerValuesForFields: LayerValuesForFields
  ): BulkPrescriptionJobs => {
    throw new Error("Method 'initializeBulkJobsWithoutOverview' must be implemented.");
  };
}

export type AnyPrescriptionJobStrategy =
  | OverviewPrescriptionJobStrategy
  | DirectPrescriptionJobStrategy;
